import type { ReviewRubric } from "../../api/client";

// Presentational 6-dimension rubric (the Review pane's instant deterministic
// score + the run's baseline → final progression). No data fetching — the
// parent owns the reviewScore call / run polling.
const DIMENSIONS: Array<{ key: keyof ReviewRubric; label: string }> = [
  { key: "codeQuality", label: "Code quality" },
  { key: "testCoverage", label: "Test coverage" },
  { key: "documentation", label: "Documentation" },
  { key: "design", label: "Design" },
  { key: "security", label: "Security" },
  { key: "ci", label: "CI" },
];

export function RubricCard({
  rubric,
  final,
}: {
  rubric: ReviewRubric;
  final?: ReviewRubric | null;
}) {
  // The score that MATTERS now is the final one when a run finished — the
  // low-dimension highlight and the "limited by" hint follow it, and the
  // baseline renders as the de-emphasized "where it started" number.
  const current = final ?? rubric;
  const min = Math.min(...DIMENSIONS.map((d) => current[d.key]));
  const limiting = DIMENSIONS.filter((d) => current[d.key] === min);
  return (
    <div className="rubric-card" data-testid="rubric-card">
      <div className="rubric-card__overall" data-testid="rubric-overall">
        <span className="rubric-card__overall-label">Overall</span>
        {final ? (
          <>
            <span className="rubric-card__overall-was">{rubric.overall} / 5</span>
            <span className="rubric-card__arrow" aria-hidden>
              →
            </span>
            <strong data-testid="rubric-overall-final">{final.overall} / 5</strong>
          </>
        ) : (
          <strong>{rubric.overall} / 5</strong>
        )}
      </div>
      <ul className="rubric-card__dims">
        {DIMENSIONS.map((d) => (
          <li
            key={d.key}
            className="rubric-card__dim"
            data-testid={`rubric-${d.key}`}
            data-low={current[d.key] === min ? "true" : undefined}
          >
            <span className="rubric-card__dim-label">{d.label}</span>
            <span className="rubric-card__dim-score">
              {final && final[d.key] !== rubric[d.key] ? (
                // compact "3 → 5" — the full "3 / 5 → 5 / 5" wraps badly in
                // the narrow grid; the /5 scale is established by Overall.
                <>
                  {rubric[d.key]}
                  <span className="rubric-card__dim-final"> → {final[d.key]}</span>
                </>
              ) : (
                <>{rubric[d.key]} / 5</>
              )}
            </span>
          </li>
        ))}
      </ul>
      {min < 5 ? (
        <p className="rubric-card__limit" data-testid="rubric-limit">
          Limited by {limiting.map((d) => d.label).join(", ")} ({min} / 5)
          {final ? " — the review comment explains why." : ""}
        </p>
      ) : null}
    </div>
  );
}
